import {
  ChangeDetectorRef,
  Component,
  EventEmitter,
  inject,
  Input,
  Output,
  signal
} from "@angular/core";
import { ButtonClickAsyncEvent } from "src/app/components/ui/button/button-click-async-event";
import { Lambda } from "src/app/support/lambda";
import { toSolidIcon } from "src/app/support/font-awesome-icons";

import { sntLog } from "src/app/common/logger";

const logger = sntLog("PageDetailToolbarIconActionComponent");

@Component({
  selector: "snt-page-detail-toolbar-icon-action",
  templateUrl: "page-detail-toolbar-icon-action.component.html",
  styles: `
    :host {
      display: flex;
    }
  `,
  standalone: false
})
export class PageDetailToolbarIconActionComponent {
  @Input()
  icon: string = "";

  @Input()
  title: string = "";

  @Input()
  iconColor: string | undefined;

  @Input()
  disabled: boolean = false;

  @Output()
  onClick = new EventEmitter<void>();

  @Output()
  onClickAsync = new EventEmitter<ButtonClickAsyncEvent>();

  protected readonly loading = signal<boolean>(false);

  readonly #changeDetectorRef = inject(ChangeDetectorRef);

  readonly #clickAsyncEvent: ButtonClickAsyncEvent = {
    lock: () => {
      this.loading.set(true);
      this.disabled = true;
    },
    unclock: () => {
      this.loading.set(false);
      this.disabled = false;
    },
    execute: async (fn: Lambda<Promise<void>>) => {
      try {
        this.#clickAsyncEvent.lock();
        await fn();
      } catch (ex) {
        logger.error(ex);
      } finally {
        this.#clickAsyncEvent.unclock();
        this.#changeDetectorRef.markForCheck();
      }
    }
  };

  click(event: MouseEvent): void {
    event.stopPropagation();
    event.stopImmediatePropagation();

    if (this.disabled || this.loading()) {
      return;
    }

    this.onClick.emit();
    this.onClickAsync.emit(this.#clickAsyncEvent);
  }

  onKeydown(event: KeyboardEvent): void {
    if (this.disabled || this.loading()) {
      return;
    }

    if (event.key !== "Enter" && event.key !== " ") {
      return;
    }

    event.preventDefault();
    event.stopPropagation();
    this.onClick.emit();
    this.onClickAsync.emit(this.#clickAsyncEvent);
  }

  displayIcon(): string {
    return toSolidIcon(this.icon);
  }

  iconStyle(): Record<string, string> | null {
    if (!this.iconColor) {
      return null;
    }

    return { color: this.iconColor };
  }
}
